import type { FeatureModule, FeatureState } from '../types';
import { getCurrentWidgetLang, t } from '../i18n/index';

export default function createKeyboardNavModule(): FeatureModule {
  let enabled = false;
  const STYLE_ID = 'accessify-keyboard-nav';
  const SKIP_LINK_ID = 'accessify-skip-link';
  const OVERLAY_ID = 'accessify-keyboard-help';
  const STORAGE_KEY = 'accessify-keyboard-nav';

  let headingIndex = -1;
  let landmarkIndex = -1;
  let headings: HTMLElement[] = [];
  let landmarks: HTMLElement[] = [];
  let helpOverlayVisible = false;
  // Element that had focus before the help overlay opened — focus returns
  // here when the overlay closes (standard modal-dialog behaviour).
  let helpOverlayReturnFocus: HTMLElement | null = null;
  // Focus-trap keydown handler, kept so it can be detached on close.
  let helpTrapHandler: ((e: KeyboardEvent) => void) | null = null;
  let mutationObserver: MutationObserver | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let originalPushState: typeof history.pushState | null = null;
  let originalReplaceState: typeof history.replaceState | null = null;
  const boundPopstateHandler = () => scheduleDynamicRescan();

  function lang(): string {
    return getCurrentWidgetLang();
  }

  // --- Skip-to-content link ---

  function findMainContent(): HTMLElement | null {
    return (
      document.querySelector('main') ||
      document.getElementById('content') ||
      document.getElementById('main-content') ||
      document.querySelector('[role="main"]') ||
      document.querySelector('h1')
    );
  }

  function hasSkipLink(): boolean {
    const links = document.querySelectorAll('a[href^="#"]');
    for (const link of links) {
      const text = (link as HTMLElement).textContent?.toLowerCase() || '';
      if (text.includes('skip') || text.includes('main content') || text.includes('navigation')) {
        return true;
      }
    }
    return false;
  }

  function injectSkipLink() {
    if (hasSkipLink()) return;
    const target = findMainContent();
    if (!target) return;

    // Ensure target has an ID for the skip link to reference
    if (!target.id) {
      target.id = 'accessify-main-content';
    }

    const skipLink = document.createElement('a');
    skipLink.id = SKIP_LINK_ID;
    skipLink.href = `#${target.id}`;
    skipLink.textContent = t('keyboard.skipToContent', lang());
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      target.setAttribute('tabindex', '-1');
      target.focus();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    document.body.insertBefore(skipLink, document.body.firstChild);
  }

  function removeSkipLink() {
    const skipLink = document.getElementById(SKIP_LINK_ID);
    skipLink?.remove();
  }

  // --- Heading navigation (Alt+H) ---

  function collectHeadings() {
    headings = Array.from(
      document.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6')
    ).filter((el) => {
      // Only visible headings
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function navigateToNextHeading() {
    collectHeadings();
    if (headings.length === 0) return;
    headingIndex = (headingIndex + 1) % headings.length;
    const heading = headings[headingIndex];
    heading.setAttribute('tabindex', '-1');
    heading.focus();
    heading.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // --- Landmark navigation (Alt+L) ---

  function collectLandmarks() {
    const selectors = [
      'header, [role="banner"]',
      'nav, [role="navigation"]',
      'main, [role="main"]',
      'aside, [role="complementary"]',
      '[role="search"]',
      '[role="form"]',
      'footer, [role="contentinfo"]',
      '[role="region"][aria-label]',
    ];
    landmarks = Array.from(
      document.querySelectorAll<HTMLElement>(selectors.join(', '))
    ).filter((el) => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  function navigateToNextLandmark() {
    collectLandmarks();
    if (landmarks.length === 0) return;
    landmarkIndex = (landmarkIndex + 1) % landmarks.length;
    const landmark = landmarks[landmarkIndex];
    landmark.setAttribute('tabindex', '-1');
    landmark.focus();
    landmark.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // --- Keyboard shortcut help overlay (Alt+K) ---

  /** The Alt key is labelled "Option" (⌥) on Apple keyboards. Showing
   *  "Alt" to a Mac user sends them looking for a key that isn't there. */
  function altKeyLabel(): string {
    const sig = typeof navigator !== 'undefined'
      ? (navigator.platform || navigator.userAgent || '')
      : '';
    return /Mac|iPhone|iPad/.test(sig) ? '⌥ Option' : 'Alt';
  }

  function toggleHelpOverlay() {
    const existing = document.getElementById(OVERLAY_ID);
    if (existing) {
      closeHelpOverlay();
      return;
    }

    helpOverlayVisible = true;
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', t('keyboard.shortcuts', lang()));
    overlay.setAttribute('aria-modal', 'true');

    overlay.innerHTML = `
      <div style="
        position: fixed; inset: 0; background: rgba(0,0,0,0.6);
        display: flex; align-items: center; justify-content: center;
        z-index: 2147483647;
      ">
        <div style="
          background: #fff; color: #222; border-radius: 12px; padding: 32px;
          max-width: 480px; width: 90%; max-height: 80vh; overflow-y: auto;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3); font-family: system-ui, sans-serif;
        " role="document">
          <h2 style="margin: 0 0 16px; font-size: 20px; color: #1a73e8;">${t('keyboard.shortcuts', lang())}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <thead>
              <tr style="border-bottom: 2px solid #e0e0e0;">
                <th style="text-align: left; padding: 8px 12px;">${t('keyboard.shortcut', lang())}</th>
                <th style="text-align: left; padding: 8px 12px;">${t('keyboard.action', lang())}</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid #f0f0f0;">
                <td style="padding: 8px 12px;"><kbd style="background:#f5f5f5;padding:2px 8px;border-radius:4px;border:1px solid #ccc;font-family:monospace;">${altKeyLabel()} + H</kbd></td>
                <td style="padding: 8px 12px;">${t('keyboard.nextHeading', lang())}</td>
              </tr>
              <tr style="border-bottom: 1px solid #f0f0f0;">
                <td style="padding: 8px 12px;"><kbd style="background:#f5f5f5;padding:2px 8px;border-radius:4px;border:1px solid #ccc;font-family:monospace;">${altKeyLabel()} + L</kbd></td>
                <td style="padding: 8px 12px;">${t('keyboard.nextLandmark', lang())}</td>
              </tr>
              <tr style="border-bottom: 1px solid #f0f0f0;">
                <td style="padding: 8px 12px;"><kbd style="background:#f5f5f5;padding:2px 8px;border-radius:4px;border:1px solid #ccc;font-family:monospace;">${altKeyLabel()} + K</kbd></td>
                <td style="padding: 8px 12px;">${t('keyboard.toggleHelp', lang())}</td>
              </tr>
              <tr style="border-bottom: 1px solid #f0f0f0;">
                <td style="padding: 8px 12px;"><kbd style="background:#f5f5f5;padding:2px 8px;border-radius:4px;border:1px solid #ccc;font-family:monospace;">Tab</kbd></td>
                <td style="padding: 8px 12px;">${t('keyboard.nextFocusable', lang())}</td>
              </tr>
              <tr>
                <td style="padding: 8px 12px;"><kbd style="background:#f5f5f5;padding:2px 8px;border-radius:4px;border:1px solid #ccc;font-family:monospace;">Escape</kbd></td>
                <td style="padding: 8px 12px;">${t('keyboard.closeOverlay', lang())}</td>
              </tr>
            </tbody>
          </table>
          <button style="
            margin-top: 20px; padding: 8px 24px; background: #1a73e8; color: #fff;
            border: none; border-radius: 6px; cursor: pointer; font-size: 14px;
          " id="accessify-keyboard-help-close">${t('widget.close', lang())}</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // --- Modal focus management ---
    // Remember what had focus so we can restore it on close.
    helpOverlayReturnFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const closeBtn = document.getElementById('accessify-keyboard-help-close');
    closeBtn?.focus();
    closeBtn?.addEventListener('click', () => closeHelpOverlay());

    // Focus trap: Tab / Shift+Tab cycle within the dialog instead of
    // leaking into the page behind it. Required for aria-modal="true" —
    // without it a screen-reader / keyboard user tabs into content they
    // were told is inert. The overlay currently has one focusable element
    // (the close button), but the logic wraps over whatever it finds so
    // it stays correct if the dialog gains more controls later.
    helpTrapHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = overlay.querySelectorAll<HTMLElement>(
        'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !overlay.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !overlay.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', helpTrapHandler, true);
  }

  /** Close the help overlay from any path (Escape, close button, toggle,
   *  feature deactivate) — removes the DOM, detaches the focus trap, and
   *  returns focus to wherever it was before the overlay opened. */
  function closeHelpOverlay() {
    const overlay = document.getElementById(OVERLAY_ID);
    overlay?.remove();
    helpOverlayVisible = false;
    if (helpTrapHandler) {
      document.removeEventListener('keydown', helpTrapHandler, true);
      helpTrapHandler = null;
    }
    if (helpOverlayReturnFocus && document.contains(helpOverlayReturnFocus)) {
      helpOverlayReturnFocus.focus();
    }
    helpOverlayReturnFocus = null;
  }

  // --- Tabindex injection for interactive elements ---

  function injectTabindex() {
    const selectors = [
      'a[href]',
      'button',
      'input',
      'select',
      'textarea',
      '[role="button"]',
      '[role="link"]',
      '[role="checkbox"]',
      '[role="radio"]',
      '[role="tab"]',
      '[role="menuitem"]',
      '[role="switch"]',
      '[contenteditable="true"]',
      'summary',
    ];

    const elements = document.querySelectorAll<HTMLElement>(selectors.join(', '));
    elements.forEach((el) => {
      // Only add tabindex if the element is not already focusable via native mechanism
      // and does not already have an explicit tabindex
      if (!el.hasAttribute('tabindex') && !isNativelyFocusable(el)) {
        el.setAttribute('tabindex', '0');
        el.dataset.accessifyTabindex = 'true';
      }
    });
  }

  function removeInjectedTabindex() {
    const elements = document.querySelectorAll<HTMLElement>('[data-accessify-tabindex="true"]');
    elements.forEach((el) => {
      el.removeAttribute('tabindex');
      delete el.dataset.accessifyTabindex;
    });
  }

  function isNativelyFocusable(el: HTMLElement): boolean {
    const tag = el.tagName.toLowerCase();
    if (['a', 'button', 'input', 'select', 'textarea'].includes(tag)) {
      return true;
    }
    if (tag === 'a' && !(el as HTMLAnchorElement).href) {
      return false;
    }
    return false;
  }

  // --- Keyboard event handler ---

  function handleKeyDown(e: KeyboardEvent) {
    if (!enabled) return;

    // Close help overlay on Escape
    if (e.key === 'Escape' && helpOverlayVisible) {
      closeHelpOverlay();
      return;
    }

    if (!e.altKey) return;

    // Resolve the shortcut letter. On macOS the Alt key IS the Option (⌥)
    // key — a text-composition modifier — so Option+H/L/K produce composed
    // characters ("˙", "@", "˚" depending on layout) and `e.key` is NOT the
    // latin letter. `e.code` ('KeyH'/'KeyL'/'KeyK') reflects the physical
    // key position and stays stable across modifiers. We check `e.code`
    // first, and fall back to `e.key` so non-QWERTY physical layouts
    // (e.g. Dvorak on Windows/Linux, where `e.key` still follows the
    // layout) keep working too.
    const matches = (letter: string, physical: string): boolean =>
      e.code === physical || e.key.toLowerCase() === letter;

    if (matches('h', 'KeyH')) {
      e.preventDefault();
      navigateToNextHeading();
    } else if (matches('l', 'KeyL')) {
      e.preventDefault();
      navigateToNextLandmark();
    } else if (matches('k', 'KeyK')) {
      e.preventDefault();
      toggleHelpOverlay();
    }
  }

  // --- Styles for skip link ---

  function getStyles(): string {
    return `
      /* accessify keyboard navigation */
      #${SKIP_LINK_ID} {
        position: fixed;
        top: -100px;
        left: 50%;
        transform: translateX(-50%);
        background: #1a73e8;
        color: #fff;
        padding: 12px 24px;
        border-radius: 0 0 8px 8px;
        font-size: 16px;
        font-family: system-ui, sans-serif;
        text-decoration: none;
        z-index: 2147483647;
        transition: top 0.2s ease;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      }
      #${SKIP_LINK_ID}:focus {
        top: 0;
        outline: 3px solid #ffdd00;
        outline-offset: 2px;
      }
    `;
  }

  function injectStyles() {
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = getStyles();
  }

  function removeStyles() {
    const styleEl = document.getElementById(STYLE_ID);
    styleEl?.remove();
  }

  // --- Dynamic re-scan for SPA navigation + lazy content ---

  function scheduleDynamicRescan() {
    if (!enabled) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      // Re-collect headings and landmarks, reset indices
      collectHeadings();
      collectLandmarks();
      headingIndex = -1;
      landmarkIndex = -1;
      // Re-inject tabindex for newly added interactive elements
      injectTabindex();
    }, 300);
  }

  function setupDynamicObserver() {
    if (mutationObserver) return;
    mutationObserver = new MutationObserver(() => scheduleDynamicRescan());
    const target = document.querySelector('main') || document.body;
    mutationObserver.observe(target, { childList: true, subtree: true });
  }

  function teardownDynamicObserver() {
    mutationObserver?.disconnect();
    mutationObserver = null;
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
  }

  function setupSPAListeners() {
    if (!originalPushState) {
      originalPushState = history.pushState;
      history.pushState = function (...args: Parameters<typeof history.pushState>) {
        originalPushState!.apply(this, args);
        scheduleDynamicRescan();
      };
    }
    if (!originalReplaceState) {
      originalReplaceState = history.replaceState;
      history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
        originalReplaceState!.apply(this, args);
        scheduleDynamicRescan();
      };
    }
    window.addEventListener('popstate', boundPopstateHandler);
  }

  function teardownSPAListeners() {
    if (originalPushState) {
      history.pushState = originalPushState;
      originalPushState = null;
    }
    if (originalReplaceState) {
      history.replaceState = originalReplaceState;
      originalReplaceState = null;
    }
    window.removeEventListener('popstate', boundPopstateHandler);
  }

  // --- Module lifecycle ---

  function activate() {
    enabled = true;
    headingIndex = -1;
    landmarkIndex = -1;

    injectStyles();
    injectSkipLink();
    injectTabindex();
    document.addEventListener('keydown', handleKeyDown);
    setupDynamicObserver();
    setupSPAListeners();
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function deactivate() {
    enabled = false;

    teardownDynamicObserver();
    teardownSPAListeners();
    document.removeEventListener('keydown', handleKeyDown);
    removeSkipLink();
    removeInjectedTabindex();
    removeStyles();

    // closeHelpOverlay() detaches the focus trap and restores focus —
    // safe to call even when the overlay isn't open.
    closeHelpOverlay();

    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'keyboard-nav',
    name: () => 'Keyboard Navigation',
    description: 'Enhanced keyboard navigation with skip links, heading/landmark jumping, and shortcut help',
    icon: 'keyboard-nav',
    category: 'motor',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'keyboard-nav',
      enabled,
    }),
    setState: (state: { enabled: boolean }) => {
      if (state.enabled) {
        activate();
      } else {
        deactivate();
      }
    },
  };
}
